import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Camera, 
  Upload, 
  X, 
  Loader2, 
  Plus,
  ArrowLeft,
  Sparkles,
  History,
  Trash2,
  Layers,
  ChevronRight,
  ScanBarcode,
  Search
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FoodScannerPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const multiScanInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('scanner');
  const [scanMode, setScanMode] = useState('select');
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [adjustedPortion, setAdjustedPortion] = useState(100);
  const [selectedMealType, setSelectedMealType] = useState('lunch');
  const [addingToLog, setAddingToLog] = useState(false);
  const [multiScanResults, setMultiScanResults] = useState([]);
  const [showMultiScanDialog, setShowMultiScanDialog] = useState(false);
  const [multiScanLoading, setMultiScanLoading] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target?.result);
        setImageFile(file);
        setScanMode('preview');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMultiScanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        setImage(e.target?.result);
        setShowMultiScanDialog(true);
        setMultiScanLoading(true);
        
        // Simulate multi-scan analysis
        setTimeout(() => {
          setMultiScanResults([
            { id: 1, name: 'Poulet grillé', calories: 165, protein: 31, carbs: 0, fat: 4, selected: true },
            { id: 2, name: 'Riz blanc', calories: 130, protein: 3, carbs: 28, fat: 0, selected: true },
            { id: 3, name: 'Légumes vapeur', calories: 45, protein: 2, carbs: 8, fat: 1, selected: true },
          ]);
          setMultiScanLoading(false);
        }, 1500);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeFood = async () => {
    if (!imageFile) return;
    
    setScanMode('analyzing');
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      
      const response = await axios.post(`${API}/food/analyze`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult(response.data);
      setScanMode('result');
      toast.success('Analyse terminée !');
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
      setScanMode('preview');
    } finally {
      setLoading(false);
    }
  };

  const scanBarcode = async () => {
    if (!barcodeInput.trim()) return;
    
    setBarcodeLoading(true);
    try {
      const response = await axios.get(`${API}/food/barcode/${barcodeInput.trim()}`, { withCredentials: true });
      setResult(response.data);
      setScanMode('result');
      toast.success('Produit trouvé !');
    } catch (error) {
      toast.error('Produit non trouvé');
    } finally {
      setBarcodeLoading(false);
    }
  };

  const addToFoodLog = async () => {
    if (!result) return;
    
    setAddingToLog(true);
    const portionMultiplier = adjustedPortion / 100;
    
    try {
      const foodData = {
        food_name: result.name || result.food_name,
        calories: Math.round((result.calories || 0) * portionMultiplier),
        protein: Math.round((result.protein || 0) * portionMultiplier),
        carbs: Math.round((result.carbs || 0) * portionMultiplier),
        fat: Math.round((result.fat || 0) * portionMultiplier),
        portion_grams: adjustedPortion,
        meal_type: selectedMealType,
        quantity: 1
      };
      
      await axios.post(`${API}/food/log`, foodData, { withCredentials: true });
      toast.success('Aliment ajouté au journal !');
      navigate('/nutrition');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setAddingToLog(false);
    }
  };

  const addMultiScanToLog = async () => {
    const selectedFoods = multiScanResults.filter(f => f.selected);
    if (selectedFoods.length === 0) return;
    
    try {
      for (const food of selectedFoods) {
        await axios.post(`${API}/food/log`, {
          food_name: food.name,
          calories: food.calories,
          protein: food.protein,
          carbs: food.carbs,
          fat: food.fat,
          meal_type: selectedMealType,
          quantity: 1
        }, { withCredentials: true });
      }
      toast.success(`${selectedFoods.length} aliments ajoutés !`);
      setShowMultiScanDialog(false);
      setMultiScanResults([]);
      setImage(null);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const reset = () => {
    setImage(null);
    setImageFile(null);
    setResult(null);
    setScanMode('select');
    setBarcodeInput('');
    setAdjustedPortion(100);
  };

  const getNutriScoreColor = (score) => {
    const colors = { 'A': 'bg-green-500', 'B': 'bg-lime-500', 'C': 'bg-yellow-500', 'D': 'bg-orange-500', 'E': 'bg-red-500' };
    return colors[score] || 'bg-muted';
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-10 px-4 pt-2 pb-3 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold">Scanner IA</h1>
            <p className="text-sm text-muted-foreground">Analysez vos aliments</p>
          </div>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="px-4 py-2 border-b border-border">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="scanner" className="flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Scanner
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Historique
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Scanner Tab */}
        <TabsContent value="scanner" className="p-4">
          {scanMode === 'select' && (
            <div className="space-y-6">
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto rounded-3xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-3">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-1">Analysez vos repas</h2>
                <p className="text-sm text-muted-foreground">Prenez une photo ou scannez un code-barres</p>
              </div>

              <div className="grid gap-3">
                <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => fileInputRef.current?.click()}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Camera className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">Prendre/Importer une photo</h3>
                      <p className="text-xs text-muted-foreground">Analyse IA d'un aliment</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:border-purple-500 transition-colors" onClick={() => multiScanInputRef.current?.click()}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                      <Layers className="w-6 h-6 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">Multi-aliments IA</h3>
                      <p className="text-xs text-muted-foreground">Détecter plusieurs aliments</p>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">NOUVEAU</Badge>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:border-accent transition-colors" onClick={() => setScanMode('barcode')}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                      <ScanBarcode className="w-6 h-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">Code-barres</h3>
                      <p className="text-xs text-muted-foreground">Recherche OpenFoodFacts</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </CardContent>
                </Card>
                
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
                <input ref={multiScanInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleMultiScanUpload} />
              </div>
            </div>
          )}

          {scanMode === 'barcode' && (
            <div className="space-y-6">
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
                  <ScanBarcode className="w-8 h-8 text-accent" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Scanner un code-barres</h2>
                <p className="text-sm text-muted-foreground">Entrez le code-barres du produit</p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Ex: 3017620422003"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value.replace(/\D/g, ''))}
                    className="flex-1 text-lg text-center tracking-widest"
                    onKeyDown={(e) => e.key === 'Enter' && scanBarcode()}
                  />
                  <Button onClick={scanBarcode} disabled={barcodeLoading || !barcodeInput.trim()} className="px-6">
                    {barcodeLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                  </Button>
                </div>

                <Button variant="outline" className="w-full" onClick={reset}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              </div>
            </div>
          )}

          {scanMode === 'preview' && image && (
            <div className="space-y-4">
              <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-black">
                <img src={image} alt="Preview" className="w-full h-full object-cover" />
              </div>
              <div className="flex gap-4">
                <Button variant="outline" className="flex-1" onClick={reset}>
                  <X className="w-4 h-4 mr-2" />Annuler
                </Button>
                <Button className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500" onClick={analyzeFood}>
                  <Sparkles className="w-4 h-4 mr-2" />Analyser
                </Button>
              </div>
            </div>
          )}

          {scanMode === 'analyzing' && (
            <div className="space-y-4">
              <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-black">
                <img src={image} alt="Analyzing" className="w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto mb-2 text-purple-500" />
                    <p className="text-white font-medium">Analyse en cours...</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {scanMode === 'result' && result && (
            <div className="space-y-4">
              {image && (
                <div className="relative aspect-video rounded-xl overflow-hidden">
                  <img src={image} alt="Food" className="w-full h-full object-cover" />
                  {result.nutriscore_grade && (
                    <div className={`absolute top-2 right-2 ${getNutriScoreColor(result.nutriscore_grade)} w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white text-xl shadow-lg`}>
                      {result.nutriscore_grade}
                    </div>
                  )}
                </div>
              )}
              
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-heading text-lg font-bold mb-3">{result.name || result.food_name}</h3>
                  
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="text-center p-2 rounded-lg bg-orange-500/10">
                      <p className="text-lg font-bold text-orange-500">{Math.round((result.calories || 0) * adjustedPortion / 100)}</p>
                      <p className="text-[10px] text-muted-foreground">kcal</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-red-500/10">
                      <p className="text-lg font-bold text-red-500">{Math.round((result.protein || 0) * adjustedPortion / 100)}g</p>
                      <p className="text-[10px] text-muted-foreground">Prot.</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-blue-500/10">
                      <p className="text-lg font-bold text-blue-500">{Math.round((result.carbs || 0) * adjustedPortion / 100)}g</p>
                      <p className="text-[10px] text-muted-foreground">Gluc.</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-yellow-500/10">
                      <p className="text-lg font-bold text-yellow-500">{Math.round((result.fat || 0) * adjustedPortion / 100)}g</p>
                      <p className="text-[10px] text-muted-foreground">Lip.</p>
                    </div>
                  </div>
                  
                  {/* Portion slider */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Portion</span>
                      <span className="font-bold">{adjustedPortion}g</span>
                    </div>
                    <input 
                      type="range" 
                      min="25" 
                      max="500" 
                      value={adjustedPortion}
                      onChange={(e) => setAdjustedPortion(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  
                  {/* Meal type selection */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Type de repas</p>
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { id: 'breakfast', label: '🌅 Petit-déj' },
                        { id: 'lunch', label: '☀️ Déjeuner' },
                        { id: 'dinner', label: '🌙 Dîner' },
                        { id: 'snack', label: '🍎 Collation' }
                      ].map(meal => (
                        <Button
                          key={meal.id}
                          variant={selectedMealType === meal.id ? "default" : "outline"}
                          size="sm"
                          className="text-xs"
                          onClick={() => setSelectedMealType(meal.id)}
                        >
                          {meal.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={reset}>Nouvelle analyse</Button>
                <Button className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500" onClick={addToFoodLog} disabled={addingToLog}>
                  {addingToLog ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Ajouter
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="p-4">
          <div className="text-center py-12">
            <History className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-muted-foreground">Aucun scan récent</p>
            <p className="text-xs text-muted-foreground mt-1">Vos analyses apparaîtront ici</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Multi-scan Dialog */}
      {showMultiScanDialog && (
        <Dialog open={true} onOpenChange={(open) => { if (!open) { setShowMultiScanDialog(false); setMultiScanResults([]); setImage(null); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-500" />
                Multi-aliments détectés
              </DialogTitle>
            </DialogHeader>
            
            {multiScanLoading ? (
              <div className="py-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-500" />
                <p className="text-sm text-muted-foreground">Analyse en cours...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {image && (
                  <img src={image} alt="Multi-scan" className="w-full h-32 object-cover rounded-lg" />
                )}
                
                {multiScanResults.map((food, index) => (
                  <div 
                    key={food.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${food.selected ? 'border-purple-500 bg-purple-500/10' : 'border-border'}`}
                    onClick={() => {
                      const updated = [...multiScanResults];
                      updated[index].selected = !updated[index].selected;
                      setMultiScanResults(updated);
                    }}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${food.selected ? 'border-purple-500 bg-purple-500' : 'border-muted-foreground'}`}>
                      {food.selected && <span className="text-white text-xs">✓</span>}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{food.name}</p>
                      <p className="text-xs text-muted-foreground">{food.calories} kcal • {food.protein}g prot.</p>
                    </div>
                  </div>
                ))}
                
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Total sélectionné</span>
                    <span className="font-bold text-orange-500">
                      {multiScanResults.filter(f => f.selected).reduce((sum, f) => sum + f.calories, 0)} kcal
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowMultiScanDialog(false); setMultiScanResults([]); setImage(null); }}>
                Annuler
              </Button>
              <Button 
                className="bg-gradient-to-r from-purple-500 to-pink-500"
                onClick={addMultiScanToLog}
                disabled={multiScanLoading || multiScanResults.filter(f => f.selected).length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter ({multiScanResults.filter(f => f.selected).length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
